<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const emit = defineEmits<{ "cta-clicked": [] }>();

const { el: capGrid, isVisible: capRevealed } = useRevealAnimation({
  threshold: 0.2,
});

const STEPS = [
  { label: "Plan", icon: "streamline:clipboard-check" },
  { label: "Code", icon: "streamline:code-monitor-2" },
  { label: "Test", icon: "streamline:test-tube" },
  { label: "Execute", icon: "streamline:button-play" },
  { label: "Live", icon: "streamline:cloud", live: true },
];

const METRICS = [
  { label: "CPU", icon: "streamline:database" },
  { label: "TIME", icon: "streamline:target-3" },
  { label: "CONTEXT", icon: "streamline:code-analysis" },
];

const PROVIDERS = [
  { name: "Claude", tag: "LLM", letter: "C", color: "#D97757", pos: "top-2 left-2" },
  { name: "OpenAI", tag: "LLM", letter: "O", color: "#10A37F", pos: "top-2 right-2" },
  { name: "Gemini", tag: "LLM", letter: "G", color: "#4B7BFF", pos: "bottom-2 left-2" },
  { name: "Custom LLM", tag: "API", letter: "+", color: "#9A6BFF", pos: "bottom-2 right-2" },
];

const CARDS = [
  {
    title: "Multi-Agent Workflow Engine",
    desc: "Chain AI agents to build, test, and execute code in real time using LangGraph and live SSE streaming.",
    visual: "workflow",
  },
  {
    title: "Isolated Subprocess Control",
    desc: "Run tasks safely in sandboxed environments with hard budget caps, timeouts, and full project context.",
    visual: "sandbox",
  },
  {
    title: "Any Model Orchestration",
    desc: "Connect any LLM provider like Claude, OpenAI, or Gemini. Get coordinated, reliable workflow execution every time.",
    visual: "router",
  },
];

/* ── Card 1 (workflow) ticking loop: progressive step accumulation ── */
const activeStep = ref(0);
let capTimer: ReturnType<typeof setInterval> | null = null;

const prefersReduced = computed(
  () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
);

const stopStepLoop = () => {
  if (capTimer) {
    clearInterval(capTimer);
    capTimer = null;
  }
};

const startStepLoop = () => {
  if (capTimer || prefersReduced.value) return;
  capTimer = setInterval(() => {
    activeStep.value = (activeStep.value + 1) % STEPS.length;
  }, 1200);
};

watch(
  capRevealed,
  (revealed) => (revealed ? startStepLoop() : stopStepLoop()),
  { immediate: true }
);

onBeforeUnmount(stopStepLoop);
</script>

<template>
  <section class="relative" aria-label="Capabilities of VaahAgents">
    <h2 class="font-avant-demi text-center text-[28px] font-semibold leading-tight lg:text-[52px]">
      <span class="bg-gradient-to-r from-[#0e7490] to-[#7c3aed] bg-clip-text text-transparent dark:from-[#12DBFF] dark:to-[#895AF5]">
        Explore the Powerful
      </span>
      <br />
      <span class="text-[21px] text-black dark:text-white lg:text-[40px]">Capabilities of VaahAgents</span>
    </h2>
    <p class="font-avant mx-auto mt-4 max-w-4xl px-4 text-center text-[16px] tracking-[0.36px] text-black/70 dark:text-white/70 lg:text-[18px]">
      Agents that work on your code, stream every move in real time, and leave a record of everything they did.
    </p>

    <div
      ref="capGrid"
      class="mx-auto mt-14 grid max-w-[1280px] grid-cols-1 gap-6 px-4 md:grid-cols-2 lg:mt-20 lg:grid-cols-3"
      :class="{ 'cap-anim': capRevealed }"
    >
      <article
        v-for="(card, cIndex) in CARDS"
        :key="card.title"
        class="rounded-2xl bg-gradient-to-b from-[#c7cdfb] to-[#eef1ff] p-[1px] shadow-xl transition-all duration-300 hover:-translate-y-1 dark:from-[#3840b5]/50 dark:to-[#0b0b26]"
        :class="{ 'md:col-span-2 lg:col-span-1': cIndex === 2 }"
      >
        <div class="flex h-full flex-col rounded-[calc(1rem-1px)] bg-gradient-to-b from-white to-[#eef2ff] p-6 backdrop-blur-xl lg:p-7 dark:from-[#080c22]/95 dark:to-[#030512]/98">
          
          <!-- Card 1 Visual: Pipeline Workflow -->
          <div v-if="card.visual === 'workflow'" class="mb-6 flex h-[210px] items-center justify-center">
            
            <div class="flex flex-col gap-3.5 w-full">
              <!-- Top Row: Boxes & Connecting Line -->
              <div class="relative flex items-center justify-between w-full h-[60px] px-2">
                
                <!-- Dead-Center Background Line (z-0 behind node boxes) -->
                <div class="absolute left-[28px] right-[28px] top-1/2 -translate-y-1/2 z-0 h-[2px] overflow-hidden bg-[#1C2B48]/10 dark:bg-white/15">
                  <div class="cap-flow-line h-full w-full bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent"></div>
                </div>

                <!-- Node Boxes (z-10 with solid bg-[#0d1430] to cover the line segment behind them) -->
                <template v-for="(step, i) in STEPS" :key="step.label">
                  <div
                    class="relative z-10 flex h-[60px] w-[60px] flex-col items-center justify-center gap-1 rounded-2xl border bg-[#f4f6ff] text-[#1C2B48]/90 shadow-md transition-all duration-300 dark:bg-[#0d1430] dark:text-white/90"
                    :class="[
                      step.live
                        ? 'border-[#c026d3] shadow-[0_0_12px_rgba(192,38,211,0.25)]'
                        : capRevealed && i <= activeStep && !prefersReduced
                          ? 'border-[#22d3ee] shadow-[0_0_12px_rgba(34,211,238,0.25)]'
                          : 'border-[#2292ee]/30'
                    ]"
                  >
                    <WrIcon :name="step.icon" class="size-5 text-[#0891b2] dark:text-[#22d3ee]" />
                    <span class="text-[11px] font-medium text-[#1C2B48]/90 dark:text-white/90">{{ step.label }}</span>
                  </div>
                </template>
              </div>

              <!-- Bottom Row: Check Circles / Live Dot Aligned with Boxes -->
              <div class="flex items-center justify-between w-full px-2">
                <template v-for="(step, i) in STEPS" :key="'check-' + step.label">
                  <div class="flex w-[60px] justify-center items-center">
                    
                    <!-- Step Check Circle Indicator -->
                    <div
                      v-if="!step.live"
                      class="flex size-5 items-center justify-center rounded-full border transition-all duration-300"
                      :class="[
                        capRevealed && (i <= activeStep || prefersReduced)
                          ? 'border-[#22d3ee] bg-[#22d3ee] shadow-[0_0_10px_rgba(34,211,238,0.6)]'
                          : 'border-[#2292ee]/40 bg-white dark:bg-[#0d1430]'
                      ]"
                    >
                      <svg
                        v-if="capRevealed && (i <= activeStep || prefersReduced)"
                        class="size-3 text-[#080c22]"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3.5 8.5l3 3 6-6" />
                      </svg>
                    </div>

                    <!-- Live Active Dot -->
                    <div
                      v-else
                      class="flex size-5 items-center justify-center rounded-full border border-[#c026d3]/60 bg-white shadow-[0_0_10px_rgba(192,38,211,0.5)] dark:bg-[#0d1430]"
                    >
                      <span class="size-2.5 rounded-full bg-[#c026d3]"></span>
                    </div>

                  </div>
                </template>
              </div>
            </div>

          </div>

          <!-- Card 2 Visual: Sandbox Subprocess Control -->
          <div v-else-if="card.visual === 'sandbox'" class="mb-6 flex h-[210px] items-center justify-center">
            <div class="flex w-full items-center justify-between gap-2">
              <div class="z-10 flex w-[84px] flex-col gap-2">
                <div v-for="m in METRICS" :key="m.label" class="flex h-[30px] items-center gap-2">
                  <span class="relative z-10 flex size-7 items-center justify-center rounded-lg border border-[#2292ee]/40 bg-[#f4f6ff] text-[#0891b2] dark:bg-[#0d1430] dark:text-[#22d3ee]">
                    <WrIcon :name="m.icon" class="size-3.5" />
                  </span>
                  <span class="text-[10px] font-medium tracking-wider text-[#5A5478]/80 dark:text-white/70">{{ m.label }}</span>
                </div>
              </div>

              <div class="relative flex h-[98px] flex-1 max-w-[60px] min-w-[30px] flex-col justify-between" aria-hidden="true">
                <div
                  v-for="mi in 3"
                  :key="mi"
                  class="relative h-[2px] w-full overflow-hidden rounded-full bg-[#1C2B48]/10 dark:bg-white/10"
                  :style="{ '--cap-i': mi - 1 }"
                >
                  <div class="cap-pipe-flow absolute inset-0 bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent"></div>
                </div>
              </div>

              <div class="relative z-10 flex w-[120px] flex-col gap-2.5 rounded-2xl border border-[#2292ee]/60 bg-[#f4f6ff] p-3 shadow-[0_0_24px_rgba(119,130,255,0.15)] dark:bg-[#0d1430]">
                <div class="flex items-center justify-center gap-1.5 border-b border-[#1C2B48]/10 pb-2 text-[9px] font-semibold tracking-widest text-[#1C2B48]/80 dark:border-white/10 dark:text-white/80">
                  <WrIcon name="streamline:shield-1" class="size-3.5 text-[#0891b2] dark:text-[#22d3ee]" />
                  SANDBOX
                </div>
                <div class="whitespace-nowrap rounded-lg bg-[#7782ff]/25 px-2.5 py-1.5 text-[10px] text-[#4348b8] dark:bg-[#7782ff]/15 dark:text-[#a5acff]">
                  ✦ Agent Process
                </div>
                <div class="flex items-center gap-1.5 rounded-lg bg-[#0d9488]/20 px-2.5 py-1.5 text-[10px] text-[#0f766e] dark:bg-[#2dd4bf]/15 dark:text-[#5eead4]">
                  <WrIcon name="streamline:check" class="size-3" />
                  Isolated
                </div>
              </div>

              <div class="relative h-[2px] flex-1 max-w-[60px] min-w-[30px] overflow-hidden rounded-full bg-[#1C2B48]/10 dark:bg-white/10" aria-hidden="true">
                <div class="cap-pipe-flow absolute inset-0 bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent" style="--cap-i: 3;"></div>
              </div>

              <div class="z-10 flex w-[80px] flex-col items-center gap-1.5">
                <div class="relative z-10 flex size-10 items-center justify-center rounded-xl border border-[#2292ee]/40 bg-[#f4f6ff] text-[#7782FF] dark:bg-[#0d1430]">
                  <WrIcon name="streamline:shield-1" class="size-5" />
                </div>
                <span class="text-center text-[9px] leading-tight text-[#5A5478]/70 dark:text-white/60">
                  Safe<br />Environment
                </span>
              </div>
            </div>
          </div>

          <!-- Card 3 Visual: Model Router Lines -->
          <div v-else class="relative mb-6 flex h-[210px] w-full items-center justify-center">
            <svg class="absolute inset-0 z-0 size-full" viewBox="0 0 360 200" preserveAspectRatio="none" aria-hidden="true">
              <path d="M 180 100 Q 110 50 65 35" fill="none" stroke="rgba(119, 130, 255, 0.3)" stroke-width="1.5" />
              <path d="M 180 100 Q 250 50 295 35" fill="none" stroke="rgba(119, 130, 255, 0.3)" stroke-width="1.5" />
              <path d="M 180 100 Q 110 150 65 165" fill="none" stroke="rgba(119, 130, 255, 0.3)" stroke-width="1.5" />
              <path d="M 180 100 Q 250 150 295 165" fill="none" stroke="rgba(119, 130, 255, 0.3)" stroke-width="1.5" />

              <path d="M 180 100 Q 110 50 65 35" class="cap-router-pulse" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round" />
              <path d="M 180 100 Q 250 50 295 35" class="cap-router-pulse" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round" />
              <path d="M 180 100 Q 110 150 65 165" class="cap-router-pulse" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round" />
              <path d="M 180 100 Q 250 150 295 165" class="cap-router-pulse" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round" />
            </svg>

            <div
              v-for="p in PROVIDERS"
              :key="p.name"
              :class="['absolute z-10 flex items-center gap-2 rounded-lg border border-[#2292ee]/40 bg-white px-2.5 py-1.5 shadow-md dark:bg-[#0d1430]', p.pos]"
            >
              <span
                class="flex size-4 items-center justify-center rounded text-[9px] font-bold"
                :style="{ color: p.color, backgroundColor: p.color + '26' }"
              >
                {{ p.letter }}
              </span>
              <div class="flex flex-col">
                <span class="text-[9px] font-medium leading-none text-[#1C2B48] dark:text-white/90">{{ p.name }}</span>
                <span class="mt-0.5 text-[7px] tracking-wider text-[#5A5478]/70 dark:text-white/50">{{ p.tag }}</span>
              </div>
            </div>

            <div class="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 rounded-xl border border-[#2292ee]/60 bg-white px-4 py-2 shadow-[0_0_20px_rgba(119,130,255,0.25)] dark:bg-[#0d1430]">
              <WrIcon name="streamline:star-1" class="size-3.5 text-[#7782FF]" />
              <span class="text-[10px] font-semibold text-[#1a1a1a] dark:text-white">VaahAgents</span>
              <span class="text-[7px] tracking-widest text-[#7782ff]">ROUTER</span>
            </div>
          </div>

          <div class="mt-auto">
            <h3 class="font-avant text-[18px] font-semibold leading-snug text-[#1a1a1a] dark:text-white">{{ card.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-[#7782ff] dark:text-[#7782ff]">{{ card.desc }}</p>
          </div>
        </div>
      </article>
    </div>

    <div class="mt-12 flex justify-center lg:mt-16">
      <WrButton append-class="px-7 py-2.5 text-base" @click="emit('cta-clicked')">
        Connect Your First Component
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.cap-anim .cap-flow-line {
  animation: cap-flow 4.5s linear infinite;
}

.cap-anim .cap-pipe-flow {
  animation: cap-flow 4s linear infinite;
  animation-delay: calc(var(--cap-i, 0) * 0.4s);
}

.cap-router-pulse {
  stroke-dasharray: 25 120;
  opacity: 0;
}

.cap-anim .cap-router-pulse {
  opacity: 0.9;
  animation: cap-pulse-path 4s linear infinite;
}

@keyframes cap-flow {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes cap-pulse-path {
  0% { stroke-dashoffset: 145; }
  100% { stroke-dashoffset: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .cap-anim .cap-flow-line,
  .cap-anim .cap-pipe-flow,
  .cap-anim .cap-router-pulse {
    animation: none !important;
  }
}
</style>